/* eslint-disable no-restricted-globals,prefer-template, no-useless-escape */
import type {UrlParamsType} from 'constants/flow/flowTypes';

/**
 * Get url for canonical link using prefix "https://www.glassesusa.com/"
 * @param {string} pathname - pathname string
 * @returns {string} - url for canonical link
 */
export const getCanonicalUrl = (pathname): string => {
    const page = (pathname || window.location.pathname).replace(/^\/+|\/+$/g, '');
    return `https://www.glassesusa.com/${page}`;
};

/**
 * Get url search string
 * @param {UrlParamsType} params - url search params
 * @returns {string} - url search string
 */
export const createUrlString = (params: UrlParamsType): string =>
    Object.entries(params)
        .reduce(
            (searchString, pair) =>
                String(pair[1]) === '' ? searchString : `${searchString}&${pair[0]}=${pair[1]}`,
            '',
        )
        .slice(1);

/**
 * Parse url params and return object
 * @param {string} searchParams - URL search parameters
 * @return {object} - url params
 */
export const getUrlParams = (searchParams?: string): {[string]: Array<string>} => {
    let getParams;
    try {
        getParams = decodeURI(searchParams || window.location.search)
            .slice(1)
            .split('&');
    } catch (error) {
        getParams = [];
    }
    const params = {};

    getParams.forEach((item: string) => {
        const arr = item.split('=');
        if (arr.length < 2) {
            return;
        }
        params[arr[0]] = arr[1].split(',');
    });

    return params;
};

/**
 * Parse url params and return object
 * @param {string} item Current name
 * @param {string} [url] - URL which will be used to search the parameter instead of BOM's location
 * @return {string} - url param
 */
export const getUrlParameter = (item: string, url?: string): string => {
    const name = item.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(url || location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

/**
 * Update search params in url
 * @param {UrlParamsType} params - url search params
 */
export function updateUrlSearchParams(params: UrlParamsType) {
    const currentParams = getUrlParams();
    history.replaceState(
        history.state,
        document.title,
        `${location.pathname}?${createUrlString({...currentParams, ...params})}`,
    );
}

/**
 * Delete param by name from the URL and history about him if it needs
 * @param {string} param - name of param
 * @param {boolean} withHistory - delete history
 * it does not support IE 10-11
 */
export function deleteParam(param = '', withHistory) {
    const url = new URLSearchParams(location.search);
    url.delete(param);
    window.history.replaceState(
        {},
        document.title,
        `${window.location.pathname}?${url.toString()}`,
    );

    if (withHistory)
        window.history.pushState(
            {},
            document.title,
            `${window.location.pathname}?${url.toString()}`,
        );
}

/**
 * Extracts SKU of a product from its URL
 * Used as quick getter instead of redux store selectors that requires the store to be fulfilled
 * @param {string} url - product url
 * @returns {string} product's SKU
 */
export function getProductSkuFromUrl(url: string) {
    const skuSubstring = url.match(/([a-zA-Z0-9-]+)\.html/);

    return skuSubstring ? skuSubstring[1] : '';
}
